import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { adminPreviewProfileMedia } from '../../api/endpoints/admin';
import styles from './ProfileDocuments.module.css';

/** Session-only bytes, keyed by owner and immutable media version; no polling. */
export function ProfileMediaPreview({
  userId,
  url,
  label,
  autoLoad = false,
  compact = false,
}: {
  userId: number;
  url: string;
  label: string;
  autoLoad?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(autoLoad);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [objectUrl, setObjectUrl] = useState<string>();
  const [renderFailed, setRenderFailed] = useState(false);
  const media = useQuery({
    queryKey: ['profile-media', userId, url],
    enabled: opened,
    queryFn: async ({ signal }) => {
      const signedUrl = await adminPreviewProfileMedia(userId, url);
      try {
        const response = await fetch(signedUrl, { signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Media unavailable');
        return { signedUrl, blob: await response.blob() };
      } catch (error) {
        if (signal.aborted) throw error;
        // Keep the authorized external link usable when CORS/network prevents embedding.
        return { signedUrl, blob: null };
      }
    },
    staleTime: (query) => (query.state.data?.blob ? Infinity : 0),
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  useEffect(() => {
    if (!media.data?.blob) {
      setObjectUrl(undefined);
      return;
    }
    const value = URL.createObjectURL(media.data.blob);
    setObjectUrl(value);
    setRenderFailed(false);
    return () => URL.revokeObjectURL(value);
  }, [media.data?.blob]);
  const photo = label === 'Profile photo';
  const title = photo ? label : `${label} document`;
  const type = media.data?.blob?.type;
  const image = !!type && /^image\/(jpeg|png|webp|gif|bmp|heic|heif)$/i.test(type);
  const pdf = type === 'application/pdf';
  const failed =
    media.isError || (!!media.data && (!media.data.blob || (!image && !pdf))) || renderFailed;
  if (compact && !expanded)
    return (
      <div className={styles.photo}>
        {objectUrl && !renderFailed ? (
          <button
            type="button"
            className={styles.avatarButton}
            aria-label={t('domain.profileMediaPreview.inspectPhoto')}
            onClick={() => setExpanded(true)}
          >
            <img
              className={styles.avatar}
              src={objectUrl}
              alt={t('domain.profileMediaPreview.photoAlt')}
              onError={() => setRenderFailed(true)}
            />
          </button>
        ) : media.isFetching ? (
          <span role="status">{t('domain.profileMediaPreview.loadingPhoto')}</span>
        ) : (
          <button type="button" onClick={() => void media.refetch()}>
            {t('domain.profileMediaPreview.retryPhoto')}
          </button>
        )}
      </div>
    );
  return (
    <article className={compact && !expanded ? styles.photo : styles.card} aria-label={title}>
      {!compact && <h4>{label}</h4>}
      {!opened && (
        <button type="button" onClick={() => setOpened(true)}>
          {t('domain.profileMediaPreview.preview', { label })}
        </button>
      )}
      {opened && media.isFetching && <p role="status">{t('domain.profileMediaPreview.loading', { label })}</p>}
      {opened && objectUrl && !renderFailed && (
        <>
          <div className={expanded ? styles.expanded : compact ? styles.avatar : styles.preview}>
            {image && (
              <img
                src={objectUrl}
                alt={title}
                onError={() => setRenderFailed(true)}
                style={expanded ? { width: `${zoom}%`, maxWidth: 'none' } : undefined}
              />
            )}
            {pdf && <iframe title={title} src={objectUrl} />}
          </div>
          {(image || pdf) && (
            <button
              type="button"
              onClick={() => {
                setExpanded(!expanded);
                setZoom(100);
              }}
            >
              {expanded ? 'Collapse' : 'Expand'} {label}
            </button>
          )}
          {expanded && image && (
            <>
              <button
                type="button"
                disabled={zoom >= 300}
                onClick={() => setZoom(zoom + 25)}
                aria-label={`Zoom in ${label}`}
              >
                +
              </button>
              <button
                type="button"
                disabled={zoom <= 100}
                onClick={() => setZoom(zoom - 25)}
                aria-label={`Zoom out ${label}`}
              >
                −
              </button>
              <span>{zoom}%</span>
            </>
          )}
        </>
      )}
      {opened && failed && (
        <p role="alert">
          {t('domain.profileMediaPreview.unavailable')}{' '}
          <button
            type="button"
            disabled={media.isFetching}
            onClick={() => {
              setRenderFailed(false);
              void media.refetch();
            }}
          >
            {t('domain.profileMediaPreview.retry', { label })}
          </button>
        </p>
      )}
      {opened && media.data && (
        <div className={styles.actions}>
          <a href={objectUrl ?? media.data.signedUrl} target="_blank" rel="noopener noreferrer">
            {t('domain.profileMediaPreview.open', { title })}
          </a>
          {objectUrl && (
            <a
              href={objectUrl}
              download={`${label}.${pdf ? 'pdf' : (type?.split('/')[1] ?? 'bin')}`}
            >
              {t('domain.profileMediaPreview.download', { label })}
            </a>
          )}
        </div>
      )}
    </article>
  );
}
